import FieldSettingsButton, { FieldImportButton } from "../field-settings-button"
import { hasFieldEditor, getFieldEditor } from "../field-editor-resolver"
import evaluateVisibleIf from "./evaluate-visibility"
import { useState } from "react"
import VariantReader from "../../VariantReader"
import defaultVariants from "../variants"
import { applyFieldModifiers } from "../field-modifier"
import FieldLabel from "./FieldLabel"
import FieldDragDropWrapper from "../field-drag-drop-wrapper"
import GroupRenderTabs from "./group-render-tabs"
import GroupRenderFoldable from "./group-render-foldable"

function GroupController({
  contentType,
  dataItem,
  save,
  parentValues,
  parentObject,
  localFieldValues,
  setLocalFieldValues,
  errors,
  setErrors,
  enableFieldSettings,
  variant = "default",
  styleOverrides,
  validationStatusProvider,
  triggerRealtimeValidation,
  fieldSearchData = { searchTerm: "", filteredFields: [], hasActiveSearch: false },
  triggerAutoSave,
  localizationToggleInProgressRef,
  hidePanel = false,
  renderGroupsAs = "tabs",
}) {
  let variants = VariantReader.useVariant({
    componentName: "CMS",
    variant,
    styleOverrides,
  })

  if (Object.keys(variants).length === 0) {
    variants = defaultVariants[variant] || defaultVariants.default
  }
  if (!variants.groups) {
    variants = { ...variants, groups: defaultVariants.default.groups }
  }

  // Track localization changes to trigger re-renders
  const [localizationChangeCounter, setLocalizationChangeCounter] = useState(0)

  // ─── Shared helpers ────────────────────────────────────────────────────────

  const matchesGroup = (item, group) => {
    const itemGroupId =
      typeof item.group === "object" ? item.group?.value : item.group
    const normalizedItem = itemGroupId || undefined
    const normalizedGroup = group.id || undefined
    return normalizedItem === normalizedGroup
  }

  const hasGroupContent = (group) => {
    if (!contentType) return false
    return (
      contentType.fields?.some((f) => matchesGroup(f, group)) ||
      contentType.editors?.some((e) => matchesGroup(e, group))
    )
  }

  const hasVisibleFieldsForGroup = (group, fieldName) => {
    if (!contentType || !contentType[fieldName]) return false
    const groupFields = contentType[fieldName]?.filter((f) => matchesGroup(f, group))
    if (!groupFields?.length) return false
    return groupFields.some((field) => {
      if (!field.visibleIf) return true
      return evaluateVisibleIf(field.visibleIf, localFieldValues, contentType)
    })
  }

  // ─── Field / editor renderers (shared) ────────────────────────────────────

  const renderComponent = (field) => {
    const Component = getFieldEditor(field, dataItem, contentType)
    if (!Component) return null

    let readOnly =
      field.localization &&
      dataItem &&
      dataItem.localization &&
      Array.isArray(dataItem.localization) &&
      dataItem.localization.find((item) => item.field === field.id)

    if (field.readOnly === true) readOnly = true

    const fieldValidationStatus = validationStatusProvider
      ? validationStatusProvider(field.id)
      : null

    const parentObjectData =
      typeof parentObject === "function" ? parentObject() : parentObject
    const rootContentType = parentObjectData?._contentType || contentType?.type

    const parentObjectWithType = () => ({
      ...localFieldValues,
      _contentType: rootContentType,
    })

    return (
      <Component
        readOnly={!!readOnly}
        parentValues={parentValues}
        parentObject={parentObjectWithType}
        field={field}
        value={localFieldValues[field.id]}
        validationStatus={fieldValidationStatus?.status}
        validationMessage={fieldValidationStatus?.message}
        save={save}
        onChange={(value) => {
          if (value === undefined) return
          const previousValue = localFieldValues[field.id]
          const updatedValues = applyFieldModifiers(
            contentType?.type,
            field.id,
            value,
            localFieldValues
          )
          const newFieldValues = { ...localFieldValues, ...updatedValues }
          setLocalFieldValues((prev) => ({ ...prev, ...updatedValues }))
          setErrors((prev) => ({ ...prev, [field.id]: null }))
          if (triggerRealtimeValidation) {
            triggerRealtimeValidation(field.id, value, newFieldValues)
          }
          if (triggerAutoSave && value !== previousValue) {
            triggerAutoSave(newFieldValues)
          }
        }}
      />
    )
  }

  const renderEditor = (editor) => {
    if (!hasFieldEditor({ type: editor.id })) return null
    const Component = getFieldEditor({ type: editor.id }, dataItem, contentType)

    let render = editor.render
    if (Array.isArray(editor.render)) {
      render = editor.render.reduce((acc, item) => {
        acc[item.key] = item.value
        return acc
      }, {})
    }
    const className = render?.variant
      ? variants.editors[render?.variant]
      : variants.editors?.default ?? defaultVariants.editors.default

    return (
      <div key={editor.id} className={className}>
        <Component
          editor={editor}
          parentObject={localFieldValues}
        />
      </div>
    )
  }

  const renderField = (field, inHiddenPanel = false) => {
    const isVisible = evaluateVisibleIf(field.visibleIf, localFieldValues, contentType)
    const hasEditor = hasFieldEditor(field)

    let render = field.render
    if (Array.isArray(field.render)) {
      render = field.render.reduce((acc, item) => {
        acc[item.key] = item.value
        return acc
      }, {})
    }

    let className
    if (render?.variant) {
      className =
        variants.fields?.[render.variant] ||
        defaultVariants.fields?.[render.variant] ||
        variants.fields?.default ||
        defaultVariants.fields.default
    } else {
      className = variants.fields?.default || defaultVariants.fields.default
    }

    const isRequired =
      typeof field.required === "function"
        ? field.required(localFieldValues)
        : field.required

    let readOnly =
      field.localization &&
      dataItem &&
      dataItem.localization &&
      Array.isArray(dataItem.localization) &&
      dataItem.localization.find((item) => item.field === field.id)

    if (field.readOnly === true) readOnly = true

    return isVisible && hasEditor ? (
      <FieldDragDropWrapper
        key={field.id}
        field={field}
        fieldValues={localFieldValues}
        setFieldValues={setLocalFieldValues}
        dataItem={dataItem}
        onSettingChange={(newDataItem) => {
          dataItem = newDataItem
          if (localizationToggleInProgressRef) {
            localizationToggleInProgressRef.current = true
          }
          requestAnimationFrame(() => {
            setLocalizationChangeCounter((prev) => prev + 1)
          })
        }}
        className={className}
      >
        <FieldLabel field={field} isRequired={isRequired} readOnly={!!readOnly} />
        {renderComponent(field)}
        {enableFieldSettings && !!field.localization && (
          <FieldSettingsButton
            contentType={contentType}
            dataItem={dataItem}
            onContentSave={save}
            field={field}
            fieldValues={localFieldValues}
            setFieldValues={setLocalFieldValues}
            onSettingChange={(newDataItem) => {
              dataItem = newDataItem
              if (localizationToggleInProgressRef) {
                localizationToggleInProgressRef.current = true
              }
              requestAnimationFrame(() => {
                setLocalizationChangeCounter((prev) => prev + 1)
              })
            }}
          />
        )}
        {enableFieldSettings && !readOnly && field.multiple && (field.type?.value || field.type) === "Text" && !field.options?.length && (
          <FieldImportButton
            field={field}
            fieldValues={localFieldValues}
            setFieldValues={setLocalFieldValues}
            dataItem={dataItem}
            onSettingChange={(newDataItem) => { dataItem = newDataItem }}
          />
        )}
        {errors[field.id] && (
          <p className="text-admin_error">{errors[field.id]}</p>
        )}
      </FieldDragDropWrapper>
    ) : null
  }

  // ─── Group body renderer (shared between tabs and foldable) ────────────────

  const renderGroupFields = (group) => (
    <div className="flex flex-row flex-wrap">
      {contentType?.fields?.filter((f) => matchesGroup(f, group)).map((f) => renderField(f))}
      {contentType?.editors?.filter((e) => matchesGroup(e, group)).map(renderEditor)}
    </div>
  )

  // ─── Search results renderer ───────────────────────────────────────────────

  const renderSearchResults = () => {
    const groupVariant = variants.groups?.default ?? defaultVariants.groups.default
    return (
      <div key="search-results" className={groupVariant.root}>
        <div className={groupVariant.panel}>
          <div className={groupVariant.panel_content}>
            <h3 className={groupVariant.panel_title}>
              Fields for &ldquo;{fieldSearchData.searchTerm}&rdquo; (
              {fieldSearchData.filteredFields.length} fields)
            </h3>
          </div>
        </div>
        <div className={groupVariant.content}>
          {fieldSearchData.filteredFields.map((f) => renderField(f))}
        </div>
      </div>
    )
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  const render = () => {
    if (!contentType) return null

    // Filter named groups that have visible content
    const namedGroups = (contentType.groups || []).filter((g) => {
      if (g.visible === false) return false
      if (!hasGroupContent(g)) return false
      const hasFields = hasVisibleFieldsForGroup(g, "fields")
      const hasEditors = hasVisibleFieldsForGroup(g, "editors")
      return hasFields || hasEditors
    })

    // Ungrouped fields sentinel (fields not assigned to any named group)
    const ungroupedGroup = {
      id: undefined,
      label: "Fields",
      description: "",
      hidePanel: hidePanel,
      _hasContent: hasGroupContent({ id: undefined }),
    }

    const shared = {
      namedGroups,
      ungroupedGroup,
      renderGroupFields,
      fieldSearchData,
      renderSearchResults,
    }

    if (renderGroupsAs === "foldable") {
      return (
        <GroupRenderFoldable
          {...shared}
          contentType={contentType}
          variant={variant}
        />
      )
    }

    return <GroupRenderTabs {...shared} />
  }

  return { render }
}

export default GroupController
